import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button, Caption, Field, Input, ProgressBar, Sheet } from "../../components/ui/ds";
import { Chk, DateField, Stopwatch } from "../../components/ui";
import { iconChipStyle, resolveIcon } from "../../lib/icons-resolve";
import { chatDayHeading, fmtDur, fmtMetric, fmtNum, parseDur, shiftDay, todayKey } from "../../lib/format";
import { METRIC_BACKFILL_DAYS, METRIC_PERIOD_SCOPE } from "../../lib/constants";
import { st } from "../../styles.js";
import "./metric-entry.css";

// MERATEĽNÝ NÁVYK („vlastný výsledok") — zápis hodnoty.
//
// Ovládanie je zámerne TO ISTÉ ako pri súťaži s vlastným výsledkom (challenges.jsx): zápis sa
// PRIPOČÍTAVA („Pridať" — poháre vody po jednom), a keď sa človek pomýli, „Opraviť súčet dňa"
// prepíše celok. Čas sa zapisuje ako 20:00 / 1:23:45 a drží sa v sekundách (parseDur/fmtDur),
// takže plank v súťaži aj meditácia v návyku znamenajú to isté číslo.
//
// Splnenie sa NEKLIKÁ — vyplýva z hodnoty (server: cieľ > 0 → hodnota ≥ cieľ, inak čokoľvek > 0),
// preto tu nie je žiadna fajka: zapíšeš 0 a splnenie zmizne samo.
//
// DVE ROZLIČNÉ ČÍSLA (nepomýliť si ich):
//   • `task.metric_today` = koľko je v PREBIEHAJÚCOM pokuse. Pri návyku „N× za obdobie" sa
//     nedokončený pokus prenáša cez polnoc (10 000 krokov 1×/týždeň: Po +2, Ut +3 → 5/10 000),
//     takže to NIE JE dnešok. Ide do veľkého čísla a do progres pruhu.
//   • `task.metric_day` = koľko je zapísané PRESNE v ten deň. Len toto sa dá prepisovať
//     (mode='set'), lebo `habit_reps` drží hodnotu per deň.
// Pri dennom/pevnom návyku sú obe rovnaké a rozdiel nie je vidieť.
//
// Dopisovanie za MINULÉ dni (najviac METRIC_BACKFILL_DAYS dozadu): deň sa vyberá kalendárom,
// hodnoty pre ten deň sa dotiahnu z `GET /api/habit-amount`. Za minulé dni sa — ako pri
// každom backfille — nedávajú body, séria áno.
export function MetricEntrySheet({ task, onSaved, onClose }) {
  const unit = task.metric_label || "";
  const isTime = !!task.metric_time;
  const isTimer = !!task.metric_timer;   // hodnotu merajú stopky (namiesto odpočítavajúceho časovača)
  const target = Number(task.metric_target) || 0;
  const period = task.metric_period || null;   // 'week'|'month'|'half'|'year' → cieľ platí na obdobie
  const scope = (period && METRIC_PERIOD_SCOPE[period]) || null;
  const tKey = todayKey();
  const [day, setDay] = useState(tKey);
  const isToday = day === tKey;
  // stav vybraného dňa: `run` = prebiehajúci pokus, `amount` = zápis toho dňa
  const [state, setState] = useState({ run: Number(task.metric_today) || 0, amount: Number(task.metric_day) || 0, done: !!task.done });
  const [val, setVal] = useState("");
  const [fix, setFix] = useState(false);      // true = „Opraviť súčet dňa" (prepíše, nepripočíta)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // prepnutie dňa musí dotiahnuť, čo tam už je — inak by „Opraviť" prepisovalo naslepo
  useEffect(() => {
    let live = true;
    setFix(false); setVal(""); setErr("");
    api(`/api/habit-amount?task_id=${task.id}&date=${day}`)
      .then((r) => { if (live) setState({ run: r.run || 0, amount: r.amount || 0, done: !!r.done }); })
      .catch(() => { /* ticho — ostane posledný známy stav */ });
    return () => { live = false; };
  }, [task.id, day]);

  async function send(amount, mode) {
    await api("/api/habit-amount", { method: "POST", body: { task_id: task.id, amount, mode, date: day } });
    onSaved();
    onClose();
  }

  async function save(e) {
    e.preventDefault();
    const n = isTime ? parseDur(val) : Number(String(val).replace(",", "."));
    if (val.trim() === "" || isNaN(n) || n < 0) {
      setErr(isTime ? "Zadaj čas — napr. 20:00 alebo 1:23:45." : "Zadaj číslo.");
      return;
    }
    setErr(""); setBusy(true);
    try { await send(n, fix ? "set" : "add"); }
    catch (e2) { setErr(e2.message); setBusy(false); }
  }

  const pct = target > 0 ? Math.min(1, state.run / target) : (state.run > 0 ? 1 : 0);
  const dayName = chatDayHeading(day, tKey).toLowerCase();
  return (
    <Sheet title={task.label} icon={resolveIcon(task.icon) || "target"} iconStyle={iconChipStyle(task.icon_color)}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Zavrieť</Button>
        <Button variant="primary" block disabled={busy} onClick={save}>{busy ? "…" : (fix ? "Uložiť" : "Pridať")}</Button>
      </>}>
      <div className="bz-metric-entry">
        <div className="bz-metric-entry__now">
          <span className="bz-metric-entry__val">{fmtMetric(state.run, unit, isTime)}</span>
          {target > 0 && <span style={st.taskDesc}>z {fmtMetric(target, unit, isTime)}{scope ? " · " + scope : ""}</span>}
          {state.done && <span style={{ ...st.taskDesc, color: "var(--success)" }}><Chk color="var(--success)" />splnené</span>}
        </div>
        {target > 0 && <ProgressBar pct={Math.round(pct * 100)} />}
        {/* dopisovanie zabudnutých dní — predvolene dnes, dozadu najviac METRIC_BACKFILL_DAYS */}
        <Field label="Deň">
          <DateField value={day} onChange={setDay} label="Deň zápisu"
            min={shiftDay(tKey, -METRIC_BACKFILL_DAYS)} max={tKey} />
        </Field>
        {/* stopky merajú výsledok — presne ako v detaile súťaže: po zastavení sa nameraný čas
            PRIPÍŠE k vybranému dňu. Pri minulom dni nemá čo merať (to sa dopisuje ručne). */}
        {isTimer && isToday && (
          <Stopwatch id={"habit" + task.id} onStop={(sec) => send(sec, "add")} />
        )}
        <form onSubmit={save}>
          <Field label={fix ? `Opraviť súčet (${dayName})` : `Pridať k dňu (${dayName})`}>
            {/* pri stopkách NEotvárať klávesnicu — hlavná akcia je „Spustiť stopky" nad poľom */}
            <Input autoFocus={!isTimer} type={isTime ? "text" : "number"} step="any" inputMode={isTime ? "text" : "decimal"}
              placeholder={isTime ? "napr. 20:00 alebo 1:23:45" : (unit || "koľko?")}
              value={val} onChange={(e) => { setVal(e.target.value); setErr(""); }} />
          </Field>
        </form>
        {!fix && state.amount > 0 && (
          <button type="button" style={st.linkBtn}
            onClick={() => { setFix(true); setVal(isTime ? fmtDur(state.amount) : fmtNum(state.amount)); }}>
            Opraviť súčet dňa ({fmtMetric(state.amount, unit, isTime)})
          </button>
        )}
        {fix && (
          <button type="button" style={st.linkBtn}
            onClick={() => { setFix(false); setVal(""); }}>Späť na pripočítavanie</button>
        )}
        <Caption>
          {target <= 0
            ? "Cieľ nemáš nastavený — splnené je hneď, ako niečo zapíšeš."
            : scope
              ? `Zápisy sa zbierajú cez dni: splnené bude, keď za ${scope} nazbieraš ${fmtMetric(target, unit, isTime)}. Potom sa počítadlo vynuluje na ďalšie opakovanie.`
              : `Splnené bude, keď v ten deň nazbieraš ${fmtMetric(target, unit, isTime)}.`}
        </Caption>
        {!isToday && <Caption>Za minulé dni sa body nedávajú — séria a štatistiky áno.</Caption>}
        {err && <div style={st.err}>{err}</div>}
      </div>
    </Sheet>
  );
}
